import React, {Component} from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import {withTranslation} from "react-i18next";
import {Colors, FontStyles} from "@stacc/flow-ui-components";
import {formatDateTime} from "../utils/dateFormatter";
import fileService from "../services/files";
import FileItem from "./FileItem";

class Summary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            variables: undefined,
            fileItems: [],
            variablesMessage: ""
        };
        // moved loading logic out of constructor into componentDidMount
        this._isMounted = false;
        this._abortController = null;
        // incremental id for attachments requests to ignore stale responses
        this._attachmentsRequestId = 0;
    }

    // helper to merge incoming file items with existing ones and dedupe by id
    mergeUniqueById(existing = [], incoming = []) {
        const map = new Map();
        // put incoming first so they appear before existing
        incoming.forEach(it => {
            if (it && it.id) map.set(it.id, it);
        });
        existing.forEach(it => {
            if (it && it.id && !map.has(it.id)) map.set(it.id, it);
        });
        return Array.from(map.values());
    }

    componentDidMount() {
        this._isMounted = true;
        this._abortController = new AbortController();
        const processInstanceId = this.props.caseData?._meta?.processInstanceId;
        if (processInstanceId) {
            this.loadCamundaVariablesIntoState(processInstanceId, this._abortController.signal);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this._abortController) {
            try {
                this._abortController.abort();
            } catch (e) {
                // ignore
            }
        }
    }

    componentDidUpdate(prevProps) {
        const prevPid = prevProps.caseData?._meta?.processInstanceId;
        const currPid = this.props.caseData?._meta?.processInstanceId;
        const prevFlowId = prevProps.caseData?.flowId;
        const currFlowId = this.props.caseData?.flowId;

        if (currPid && currPid !== prevPid) {
            // abort previous controller and create a fresh one for the new request
            if (this._abortController) {
                try {
                    this._abortController.abort();
                } catch (e) {
                }
            }
            this._abortController = new AbortController();
            // clear attachments immediately when switching to a new process
            this.setState({fileItems: []});
            this.loadCamundaVariablesIntoState(currPid, this._abortController.signal);
        }

        // If flowId changes, reload attachments (they are flow-specific)
        if (currFlowId && currFlowId !== prevFlowId) {
            // clear attachments immediately when changing flow
            this.setState({fileItems: []});
            this.loadAttachmentsMetaDataIntoState(this.state.variables);
        }
    }

    loadCamundaVariablesIntoState(processInstanceId, signal) {
        // fetch variables from the Camunda API.
        if (processInstanceId) {
            fetch(`/engine-rest/history/variable-instance?processInstanceId=${processInstanceId}&sortBy=variableName&sortOrder=asc`, {signal})
                .then(response => response.json())
                .then(data => {
                    // only proceed when mounted
                    if (!this._isMounted) return;
                    this.loadAttachmentsMetaDataIntoState(data);
                    this.setState({variables: data});
                })
                .catch(error => {
                    if (error && error.name === 'AbortError') return; // expected on unmount/abort
                    if (!this._isMounted) return;
                    this.setState({
                        variables: undefined,
                        variablesMessage: error?.message || String(error),
                        fileItems: []
                    });
                });
        }
    }

    loadAttachmentsMetaDataIntoState(variables) {
        const caseData = this.props.caseData || {};
        const data = caseData.data || {};
        const company = data.company || {};
        const isDeclarationFlow = caseData.flowDefinitionId === "company-customer-declaration";
        const organizationNumber = isDeclarationFlow ?
            (company?.organizationNumber?.value ? company.organizationNumber.value : false) :
            (data?.companyOrgNumber ? data.companyOrgNumber : false);
        const bank = data?.bank?.orgId ? data.bank.orgId : false;
        const isDocAttachmentsEnabled = Boolean(variables
            && Array.isArray(variables)
            && variables.some(variable => variable.name === 'docsAttachmentsUploadEnabled' && variable.value === true));

        // If this flow is not declaration, ensure we don't show prior attachments
        if (!isDeclarationFlow) {
            if (this._isMounted) this.setState({fileItems: []});
            return;
        }

        // increment attachments request id and capture locally so stale responses are ignored
        const localRequestId = ++this._attachmentsRequestId;

        if (isDocAttachmentsEnabled) {
            fileService.getDocAttachmentsFiles({
                bank: bank,
                userPid: data.customer?.customerInformation?.id,
                organizationNumber: organizationNumber,
                flowId: caseData.flowId,
                created: caseData.createdAt
            })
                .then(files => {
                    // ignore if unmounted or stale response
                    if (!this._isMounted) return;
                    if (localRequestId !== this._attachmentsRequestId) return;

                    const fileItems = (files || []).map(file => ({
                        id: file.id,
                        name: file.name,
                        type: "docAttachment",
                        fileType: file.fileExtension.toLowerCase() === 'pdf' ? 'pdf' : 'image'
                    }));
                    if (data?.documentId) {
                        const declarationForm = {
                            id: data.documentId,
                            name: 'Declaration Form.pdf',
                            type: 'docAttachment',
                            base64EncodedDocAttachment: true,
                            fileType: 'pdf'
                        };
                        fileItems.push(declarationForm);
                    }

                    this.setState(prev => ({fileItems: this.mergeUniqueById(prev.fileItems, fileItems)}));
                }).catch(() => {
                // do not crash the component when attachments fail to load
                if (!this._isMounted) return;
                // ignore stale errors
                if (localRequestId !== this._attachmentsRequestId) return;
                console.log(`Error fetching document attachments for flowId ${caseData.flowId}`);
            });
        } else {
            // fetch from file attachments from declarationForm
            fileService.getAttachments(caseData.flowId)
                .then(attachments => {
                    // ignore if unmounted or stale response
                    if (!this._isMounted) return;
                    if (localRequestId !== this._attachmentsRequestId) return;

                    const fileItems = (attachments || []).map(attachment => ({
                        id: attachment.storeId,
                        name: attachment.fileName,
                        type: "attachment"
                    }));
                    if (data?.documentId) {
                        const declarationForm = {
                            id: data.documentId,
                            name: 'Declaration Form.pdf',
                            type: 'attachment'
                        };
                        fileItems.push(declarationForm);
                    }

                    this.setState(prev => ({fileItems: this.mergeUniqueById(prev.fileItems, fileItems)}));
                }).catch((err) => {
                // ignore stale errors
                if (!this._isMounted) return;
                if (localRequestId !== this._attachmentsRequestId) return;
                console.log(`Error fetching declaration form attachments for flowId ${caseData.flowId}`, err);
            });
        }

    }

    render() {
        const caseData = this.props.caseData;
        if (!caseData.data) {
            return (<div/>);
        }
        switch (caseData.flowDefinitionId) {
            case "company-customer-declaration":
            case "corporate-periodic-renewal":
            case "corporate-periodic-renewal2": {
                return this.renderDeclarationOrPeriodicRenewalCase();
            }
            case "cleanup-files-and-flows": {
                return this.renderCleanupCase();
            }
            default:
                throw new Error("Unsupported flowDefinitionId " + caseData.flowDefinitionId);
        }
    }

    /**Display the process variables if the Camunda API is available at localhost:8080*/
    renderVariables() {
        if (this.state.variables) {
            return (
                <div style={{marginLeft: 10, marginRight: -30}}>
                    <Title2>Variables</Title2>
                    <VariablesTable>
                        <thead>
                        <tr>
                            <VariablesTableTh>Name</VariablesTableTh>
                            <VariablesTableTh>Value</VariablesTableTh>
                        </tr>
                        </thead>
                        <tbody>
                        {
                            this.state.variables
                                .filter(variable => variable.name !== '_staccMeta')
                                .map((variable, index) => (
                                    <tr key={`${variable.name}-${index}`}>
                                        <VariablesTableTd>{variable.name}</VariablesTableTd>
                                        <VariablesTableTd>{JSON.stringify(variable.value)}</VariablesTableTd>
                                    </tr>))
                        }
                        </tbody>
                    </VariablesTable>
                </div>
            );
        }
    }

    renderTasksWithVariables() {
        const caseData = this.props.caseData;
        const tasksWithVariables = caseData.tasks?.filter(task => task.hasOwnProperty("variables"));
        if (tasksWithVariables) {
            return (<TaskVariables>
                <Title>Tasks with variables</Title>
                <List>
                    {tasksWithVariables.map(task =>
                        <li key={task.taskId}>
                            <ListTitle>{task.taskType}</ListTitle>
                            <pre>{JSON.stringify(task.variables, undefined, 2)}</pre>
                        </li>)}
                </List>
            </TaskVariables>);
        }
    }

    renderAttachments(bpiClientConfig) {
        return this.state.fileItems && this.state.fileItems.length > 0 && (
            <div style={{marginBottom: "30px"}}>
                <ListTitle>{"Filer"}</ListTitle>
                <List>{this.state.fileItems.map(item =>
                    <li key={item.id}>
                        <FileItem {...item} bpiClientConfig={bpiClientConfig}/>
                    </li>
                )}</List>
            </div>
        );
    }

    renderCleanupCase() {
        const caseData = this.props.caseData;
        const created = caseData.createdAt
            ? formatDateTime(new Date(caseData.createdAt))
            : false;
        return (<div>
                <CaseSummary>
                    <EntryFrame>
                        <Title> flowId </Title>
                        <p>{caseData.flowId}</p>
                    </EntryFrame>
                    <EntryFrame>
                        <Title>{"Opprettet"}</Title>
                        <SummaryP>{created}</SummaryP>
                    </EntryFrame>
                </CaseSummary>
            </div>
        )
    }

    renderDeclarationOrPeriodicRenewalCase() {
        const caseData = this.props.caseData;
        const t = this.props.t;
        const referenceID = caseData.referenceId ? caseData.referenceId : false;
        const flowDefinitionId = caseData.flowDefinitionId ? caseData.flowDefinitionId : false;
        const company = caseData.data.company;
        const isDeclarationFlow = caseData.flowDefinitionId === "company-customer-declaration";

        const companyName = isDeclarationFlow ?
            (company.name.value ? company.name.value : false) :
            (caseData.data.companyName ? caseData.data.companyName :
                    (caseData.data.customer?.customerInformation.name ? caseData.data.customer.customerInformation.name : false)
            );
        const organizationNumber = isDeclarationFlow ?
            (company.organizationNumber.value ? company.organizationNumber.value : false) :
            (caseData.data.companyOrgNumber ? caseData.data.companyOrgNumber : false);
        const bank = caseData.data.bank.orgId ? caseData.data.bank.orgId : false;
        const updated = caseData.updatedAt ? formatDateTime(new Date(caseData.updatedAt)) : false;
        const created = caseData.createdAt ? formatDateTime(new Date(caseData.createdAt)) : false;
        const processType = isDeclarationFlow ?
            (caseData.data.process.type === 'renewal' ?
                'Fornyelse' :
                (caseData.data.process.companyUnderEstablishment ?
                    'Onboarding SUS' :
                    'Onboarding')) : flowDefinitionId;
        const bpiClientConfig = {
            bank: bank,
            userPid: caseData.data.customer?.customerInformation.id,
            organizationNumber: organizationNumber,
            flowId: caseData.flowId,
            created: caseData.createdAt
        }
        return (<>
                <CaseSummary>
                    <EntryFrame>
                        <Title>{"Opprettet"}</Title>
                        <SummaryP>{created} </SummaryP>
                    </EntryFrame>
                    <EntryFrame>
                        <Title>{"Sist oppdatert"}</Title>
                        <SummaryP>{updated}</SummaryP>
                    </EntryFrame>
                    <EntryFrame>
                        <Title>{"Prosess"}</Title>
                        <SummaryP>{t(processType)}</SummaryP>
                    </EntryFrame>
                    <EntryFrame>
                        <Title>{"Referanse"}</Title>
                        <SummaryP>{`${referenceID}`}</SummaryP>
                    </EntryFrame>
                </CaseSummary>
                <CaseSummary>
                    <EntryFrame>
                        <Title> {"Bank"}</Title>
                        <SummaryP>{`${bank}`}</SummaryP>
                    </EntryFrame>
                    <EntryFrame>
                        <Title>{"Selskap"}</Title>
                        <SummaryP>{`${companyName}`}</SummaryP>
                    </EntryFrame>
                    <EntryFrame>
                        <Title>{"Selskap OrgNr"}</Title>
                        <SummaryP>{`${organizationNumber}`}</SummaryP>
                    </EntryFrame>
                </CaseSummary>
                {this.renderVariables()}
                {this.renderTasksWithVariables()}
                {this.renderAttachments(bpiClientConfig)}
            </>
        );
    }
}


const List = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;

    li {
        border-bottom: 1px solid ${Colors.Grey3};
    }

    li:first-of-type {
        border-top: 1px solid ${Colors.Grey3};
    }
`;

const ListTitle = styled.p`
    ${FontStyles.Small};
    margin: 0;
    padding-left: 15px;
    padding-bottom: 15px;
`;

const CaseSummary = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-auto-columns: minmax(200px, auto);
    grid-auto-rows: minmax(20px, auto);
    width: 100%;
    min-width: 550px;
    min-height: 105px;
    justify-items: start;
    padding: 10px;
    margin: 10px;
    margin-top: 30px;
    margin-bottom: 30px;
    background-color: #f7fbff;
    border: 1px solid #e6e4e3;
    border-radius: 4px;
`;

const TaskVariables = styled.div`
    position: relative;
    width: 100%;
    min-width: 550px;
    min-height: 105px;
    justify-items: start;
    padding: 10px;
    margin: 10px;
    margin-top: 30px;
    margin-bottom: 30px;
    background-color: #f7fbff;
    border: 1px solid #e6e4e3;
    border-radius: 4px;
`;

const EntryFrame = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 15px;
    margin-top: 30px;
    margin-bottom: 0;
`;
const Title = styled.p`
    ::first-letter {
        text-transform: capitalize;
    }

    color: #bbb;
    font-size: 12px;
    margin-bottom: 8px
`;

const Title2 = styled.p`
    ${FontStyles.LargeStrong};
`;

const VariablesTable = styled.table`
    font-family: arial, sans-serif;
    border-collapse: collapse;
    width: 100%;

    tr:nth-child(even) {
        background-color: #f7fbff;
    }
`;

const VariablesTableTd = styled.td`
    border: 1px solid #dddddd;
    text-align: left;
    padding: 8px;
`;

const VariablesTableTh = styled.th`
    border: 1px solid #dddddd;
    text-align: left;
    padding: 8px;
`;
const SummaryP = styled.p`
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 8px;
`;

Summary.propTypes = {
    t: PropTypes.func.isRequired,
    caseData: PropTypes.shape({
        data: PropTypes.shape({})
    }).isRequired
};

export default withTranslation("inbox")(Summary);
